import { ChangeEvent, FormEvent, FunctionComponent, ReactElement, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Checkbox, FormControlLabel, Link } from '@mui/material';
import PasswordField from '@/components/ui/form-inputs/PasswordField';
import LoadingButton from '@/components/ui/loading-button';
import { isValidPassword, requiredField } from '@/shared/utils/validation';
import { PASSWORDS_DO_NOT_MATCH, INVALID_PASSWORD } from '@/shared/utils/validation/constants';
import { Stack } from '@mui/system';
import { useUpdatePassword } from '@/queries/auth';
import { CHANGE_PASSWORD_FORM_KEY } from '@/shared/constants/form-keys';
import AuthCardLayout from '../layouts/auth-card-layout/AuthCardLayout';
import AuthLayout from '../layouts/auth-layout/AuthLayout';

interface ChangePasswordProps {
	type: 'create' | 'update';
	token: string;
	navigateAfterSuccess?: string;
}

const TERMS_AND_CONDITIONS_LINK = 'https://www.leviant.com/privacy-policy';

const ChangePassword: FunctionComponent<ChangePasswordProps> = ({
	type,
	token,
	navigateAfterSuccess,
}): ReactElement => {
	const navigate = useNavigate();

	const [newPassword, setNewPassword] = useState('');
	const [confirmNewPassword, setConfirmNewPassword] = useState('');
	const [newPasswordError, setNewPasswordError] = useState('');
	const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');
	const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] = useState(false);

	const { isLoading, mutate: updatePassword } = useUpdatePassword({
		onSuccess: () => {
			if (navigateAfterSuccess) {
				navigate(navigateAfterSuccess, { replace: true });
			}
		},
	});

	const isNewPasswordValid = (): boolean => {
		if (newPassword.length === 0) {
			setNewPasswordError(requiredField('New password'));
			return false;
		}

		if (!isValidPassword(newPassword)) {
			setNewPasswordError(INVALID_PASSWORD);
			return false;
		}

		setNewPasswordError('');
		return true;
	};

	const isConfirmNewPasswordValid = (): boolean => {
		if (newPassword !== confirmNewPassword) {
			setConfirmNewPasswordError(PASSWORDS_DO_NOT_MATCH);
			return false;
		}

		if (confirmNewPassword.length === 0) {
			setConfirmNewPasswordError(requiredField('Confirm password'));
			return false;
		}

		setConfirmNewPasswordError('');
		return true;
	};

	const changePasswordAction = (event: FormEvent) => {
		event.preventDefault();
		const newPasswordValid = isNewPasswordValid();
		const confirmNewPasswordValid = isConfirmNewPasswordValid();

		if (!newPasswordValid || !confirmNewPasswordValid) {
			return;
		}

		updatePassword({
			request: {
				request: { password: newPassword, token },
				formKey: CHANGE_PASSWORD_FORM_KEY,
			},
		});
	};

	const isDisabled = useMemo(
		() => (type === 'create' ? !acceptedTermsAndConditions : false),
		[acceptedTermsAndConditions, type]
	);

	return (
		<Box component="section" role="main">
			<AuthLayout>
				<AuthCardLayout title="Create new password">
					<Box component="form" onSubmit={changePasswordAction} mt={3}>
						<Stack direction="column" spacing={3}>
							<Stack direction="column" spacing={2}>
								<PasswordField
									id="newPassword"
									name="newPassword"
									label="New password"
									formKey={CHANGE_PASSWORD_FORM_KEY}
									fullWidth
									required
									value={newPassword}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										setNewPassword(event.target.value)
									}
									clientErrors={newPasswordError}
								/>
								<PasswordField
									id="confirm-password"
									name="confirmPassword"
									label="Re-enter password"
									formKey={CHANGE_PASSWORD_FORM_KEY}
									fullWidth
									required
									value={confirmNewPassword}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										setConfirmNewPassword(event.target.value)
									}
									clientErrors={confirmNewPasswordError}
								/>
								{type === 'create' && (
									<FormControlLabel
										control={
											<Checkbox
												value={acceptedTermsAndConditions}
												onChange={() => setAcceptedTermsAndConditions(!acceptedTermsAndConditions)}
												sx={{ alignSelf: 'start', paddingTop: '2px' }}
											/>
										}
										label={
											<Box>
												I have read and agree to the{' '}
												<a href={TERMS_AND_CONDITIONS_LINK} target="_blank">
													Terms and Conditions
												</a>
											</Box>
										}
									/>
								)}
								<Link component={RouterLink} to="/auth/login">
									Back to log in
								</Link>
							</Stack>

							<LoadingButton
								loading={isLoading}
								variant="contained"
								size="large"
								fullWidth
								type="submit"
								disabled={isDisabled}
							>
								{type === 'create' ? 'Create password' : 'Update password'}
							</LoadingButton>
						</Stack>
					</Box>
				</AuthCardLayout>
			</AuthLayout>
		</Box>
	);
};

export default ChangePassword;
